import {Component, TextPropertyEditor, FontAwesomeIcon} from 'substance'
import PlaceholderComponent from './components/PlaceholderComponent'

/**
 * @typedef UIFieldEditor.props
 * @property {string|string[]} field - Name of, or path to, the node field to edit
 * @property {Node} node - The node
 * @property {string} placeholder - Placeholder of the field
 * @property {boolean} [multiline=false] - If set to true enables multine support for field
 * @property {boolean} [disabled=false] - Set to true to disable field
 * @property {string} [icon] - Icon to display with the field
 */

/**
 * @typedef UIFieldEditor.state
 * @property {boolean} [hasFocus=false] - If the editor has focus
 */

/**
 * An editor component that lets the user edit text fields on a node.
 *
 * @name UIFieldEditor
 * @class
 *
 * @see {@link UIDatetimeFieldEditor} for datetime editing
 *
 * @example
 * import {UIFieldEditor} from 'writer'
 *
 * render($$) {
 *     const el = $$('div')
 *     return el.append(
 *         $$(UIFieldEditor, {
 *             node: this.props.node,
 *             multiLine: false,
 *             field: 'title',
 *             placeholder: this.getLabel('Add title here')
 *         })
 *     )
 * }
 *
 * @param {UIFieldEditor.props} props
 * @property {UIFieldEditor.props} props
 * @property {UIFieldEditor.state} state
 */
class FieldEditorComponent extends Component {

    getPath() {
        return this.fieldPath
    }

    getInitialState() {
        return {
            hasFocus: false
        }
    }

    get fieldValue() {
        return this.props.node.document.get(this.fieldPath)
    }

    get fieldPath() {
        const field = this.props.field
        const node = this.props.node
        return Array.isArray(field) ? [node.id, ...field] : [node.id, field]
    }

    get placeholderVisible() {
        return !this.state.hasFocus && this.fieldValue.length === 0
    }

    didUpdate() {
        this.refs['placeholder'].extendState({
            visible: this.placeholderVisible
        })
    }

    shouldRerender(newProps) {
        // Only rerender when the component's node.id changes, or the component is disabled/enabled
        return this.props.node.id !== newProps.node.id || this.props.disabled !== newProps.disabled
    }

    render($$) {
        if (!this.props.field || !this.props.node || !this.props.placeholder) {
            console.warn('Missing props, field, placeholder or node')
            return $$('span')
        }

        const field = this.props.field
        const placeholder = this.props.placeholder

        const fieldContainer = $$('div').addClass('im-blocknode__content full-width im-field-editor')
        const inputPlaceholder = $$(PlaceholderComponent, {
            placeholder,
            visible: this.placeholderVisible
        }).ref('placeholder')

        const fieldEditor = $$(TextPropertyEditor, {
            path: this.fieldPath,
            doc: this.props.node.document,
            multiLine: Boolean(this.props.multiLine),
            disabled: Boolean(this.props.disabled)
        })
            .ref(field)
            .on('blur', () => {
                this.extendState({hasFocus: false})
            })
            .on('focus', () => {
                this.extendState({hasFocus: true})
            })

        const inputContainer = $$('div').append([fieldEditor, inputPlaceholder])
        const icon = $$(FontAwesomeIcon, {icon: this.props.icon ? this.props.icon : 'fa-header'})

        fieldContainer.append([icon, inputContainer])

        return fieldContainer
    }
}

export {FieldEditorComponent}
